/**
 * useTables Hook
 * React hook for managing restaurant tables data
 */

import { useState, useEffect, useCallback } from 'react';
import { tablesService, RestaurantTable, CreateTableDto, UpdateTableDto } from '../services/tables.service';
import { ordersService } from '../services/orders.service';

interface UseTablesState {
  tables: RestaurantTable[];
  loading: boolean;
  submitting: boolean;
  error: string | null;
}

export function useTables(tenantId?: string, branchId?: string) {
  const [state, setState] = useState<UseTablesState>({
    tables: [],
    loading: false,
    submitting: false,
    error: null,
  });

  const fetchTables = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      console.log('useTables - Fetching with:', { tenantId, branchId });
      const response = await tablesService.getTables(tenantId, branchId);
      console.log('useTables - Response:', response);
      
      let tablesData: RestaurantTable[] = [];
      if (response.data) {
        tablesData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any).data || [];
      }

      // Fetch orders to compute table status
      try {
        const ordersResponse = await ordersService.getOrders({ 
          page: 1, 
          limit: 1000 // Get all orders
          // Note: Removed status filter - we'll filter on frontend instead
        });
        
        const allOrders = ordersResponse.data?.data || [];
        
        // Filter to only active orders (not delivered, closed, or cancelled)
        const activeOrders = allOrders.filter(order => 
          ['PENDING', 'CONFIRMED', 'PREPARING', 'READY'].includes(order.status)
        );
        
        console.log('useTables - Active orders:', activeOrders);
        console.log('useTables - Checking table status for tables:', tablesData.map(t => t.name));
        
        // Compute status for each table based on order notes (since we don't have tableId in orders yet)
        tablesData = tablesData.map(table => {
          // Check if any active order mentions this table in notes
          // Format from CreateOrderModal: "Table: TableName" or "table: tablename"
          const tableOrders = activeOrders.filter(order => {
            if (!order.notes) return false;
            
            const notesLower = order.notes.toLowerCase();
            const tableLower = table.name.toLowerCase();
            
            // Check multiple patterns:
            // 1. "Table: TableName"
            // 2. "table TableName"
            // 3. Just the table name at the start
            const patterns = [
              `table: ${tableLower}`,
              `table ${tableLower}`,
              tableLower,
            ];
            
            const matches = patterns.some(pattern => notesLower.includes(pattern));
            
            if (matches) {
              console.log(`useTables - Found order ${order.orderNumber} for table ${table.name}`);
            }
            
            return matches;
          });
          
          console.log(`useTables - Table ${table.name}: ${tableOrders.length} active orders`);
          
          return {
            ...table,
            status: tableOrders.length > 0 ? 'occupied' : 'available',
            activeOrdersCount: tableOrders.length
          };
        });
      } catch (orderErr) {
        console.error('Failed to fetch orders for table status:', orderErr);
        // If orders fetch fails, mark all tables as available
        tablesData = tablesData.map(table => ({
          ...table,
          status: 'available' as const,
          activeOrdersCount: 0
        }));
      }

      console.log('useTables - Processed tables with status:', tablesData);
      
      // Filter out inactive tables - only show active tables for selection in menus/orders
      const activeTables = tablesData.filter(table => table.isActive);
      console.log('useTables - Filtered to active tables:', activeTables.map(t => `${t.name} (active=${t.isActive})`));
      
      setState({ tables: activeTables, loading: false, submitting: false, error: null });
    } catch (err: any) {
      console.error('Failed to fetch tables:', err);
      setState({
        tables: [],
        loading: false,
        submitting: false,
        error: err.message || 'Failed to load tables',
      });
    }
  }, [tenantId, branchId]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const createTable = useCallback(async (data: CreateTableDto) => {
    setState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      await tablesService.createTable(data);
      await fetchTables();
      setState((prev) => ({ ...prev, submitting: false }));
    } catch (err: any) {
      setState((prev) => ({ ...prev, submitting: false, error: err.message }));
      throw err;
    }
  }, [fetchTables]);

  const updateTable = useCallback(async (id: string, data: UpdateTableDto) => {
    setState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      await tablesService.updateTable(id, data);
      await fetchTables();
      setState((prev) => ({ ...prev, submitting: false }));
    } catch (err: any) {
      setState((prev) => ({ ...prev, submitting: false, error: err.message }));
      throw err;
    }
  }, [fetchTables]);

  const deleteTable = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      await tablesService.deleteTable(id);
      await fetchTables();
      setState((prev) => ({ ...prev, submitting: false }));
    } catch (err: any) {
      setState((prev) => ({ ...prev, submitting: false, error: err.message }));
      throw err;
    }
  }, [fetchTables]);

  const refresh = useCallback(() => {
    fetchTables();
  }, [fetchTables]);

  return {
    tables: state.tables,
    loading: state.loading,
    submitting: state.submitting,
    error: state.error,
    createTable,
    updateTable,
    deleteTable,
    refresh,
  };
}